import { PillRadio, SkeletonBlock, SplitView, Tabs, TabsContent, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { HexColorPicker } from 'react-colorful';
import { useSearchParams } from 'react-router-dom';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { PageErrorState } from '../components/shared/PageErrorState';
import { ApiKeysTab } from '../features/machine-credentials/ApiKeysTab';
import {
  ResourceRolesPanel,
  type RoleDescriptionFocus,
} from '../features/resource-roles/ResourceRolesPanel';
import { StaffingGrid } from '../features/resource-roles/StaffingGrid';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useProtectedImageUrl } from '../hooks/useProtectedImageUrl';
import { apiFetch, getAuthToken } from '../lib/apiClient';
import './workspace-settings-page.css';

const WORKSPACE_TABS = [
  { value: 'brand-assets', label: 'Brand Assets' },
  { value: 'brand-colors', label: 'Brand Colors' },
  { value: 'resources', label: 'Resources' },
  { value: 'api-keys', label: 'API Keys' },
] as const;

interface WorkspaceProductOption {
  uuid: string;
  productKey: string;
  productName: string;
  isPrimary: boolean;
}

// ── Resources tab ─────────────────────────────────────────────────────────────
// Product selector (roles are per-product; the workspace-global catalog is the source of truth)
// over the reusable ResourceRolesPanel, then the staffing grid (features.staffing_editor).
function ResourcesTab() {
  // `?product=<key>` preselects a product - the deal page's "Set rates" pop-up links here.
  const [searchParams] = useSearchParams();
  const requestedProductKey = searchParams.get('product');
  const { workspace } = useWorkspace();
  // Bumped when a role changes, so the staffing grid shows current rates.
  const [rolesVersion, setRolesVersion] = React.useState(0);
  // The staffing grid's "Edit description" opens that role + category in the roles list.
  const [descriptionFocus, setDescriptionFocus] = React.useState<RoleDescriptionFocus | null>(null);
  const [products, setProducts] = React.useState<WorkspaceProductOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    apiFetch<{ products: WorkspaceProductOption[] }>('/api/workspace/admin/products')
      .then((res) => {
        if (cancelled) return;
        setProducts(res.products);
        const requested = res.products.find((p) => p.productKey === requestedProductKey);
        const primary = res.products.find((p) => p.isPrimary) ?? res.products[0];
        setSelectedKey((requested ?? primary)?.productKey ?? null);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestedProductKey]);

  if (loading) return <SkeletonBlock height="120px" />;
  if (error) return <p className="rr-tab__empty">Failed to load products.</p>;
  if (products.length === 0)
    return (
      <p className="rr-tab__empty">Add a product to this workspace to manage its resource roles.</p>
    );

  const showStaffing = !!workspace?.staffingEditorEnabled;
  return (
    <div className={showStaffing ? 'rr-tab rr-tab--with-staffing' : 'rr-tab'}>
      <div className="rr-tab__toolbar">
        <PillRadio
          options={products.map((p) => ({ value: p.productKey, label: p.productName }))}
          selectedValue={selectedKey}
          onSelectionChange={(v) => {
            // Single-select: keep a product always selected (ignore deselect-to-null).
            if (v) {
              setSelectedKey(v);
              setDescriptionFocus(null);
            }
          }}
        />
      </div>
      {selectedKey && (
        <div className="rr-section__header">
          <h3 className="rr-section__title">Roles and rates</h3>
          <p className="rr-section__note">
            Everyone who can work on this product&apos;s deals, and their hourly rates.
          </p>
        </div>
      )}
      {selectedKey && (
        <ResourceRolesPanel
          key={selectedKey}
          productKey={selectedKey}
          onRolesChanged={() => setRolesVersion((v) => v + 1)}
          focus={descriptionFocus}
        />
      )}
      {selectedKey && showStaffing && (
        <StaffingGrid
          key={`staffing-${selectedKey}`}
          productKey={selectedKey}
          reloadToken={rolesVersion}
          onEditDescription={(roleKey, categoryKey) =>
            setDescriptionFocus((prev) => ({ roleKey, categoryKey, token: (prev?.token ?? 0) + 1 }))
          }
        />
      )}
    </div>
  );
}

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_BYTES = 5 * 1024 * 1024;

interface BrandAsset {
  uuid: string;
  originalFileName: string;
  mimeType: string;
  sizeBytes: number;
  widthPx: number | null;
  heightPx: number | null;
  contentUrl: string;
  createdAt: string;
}

// ── Private sub-components ────────────────────────────────────────────────────

interface AssetCardProps {
  asset: BrandAsset;
  isSelected: boolean;
  isHeaderLogo: boolean;
  isDocLogo: boolean;
  onClick: () => void;
}

function AssetCard({ asset, isSelected, isHeaderLogo, isDocLogo, onClick }: AssetCardProps) {
  const src = useProtectedImageUrl(asset.contentUrl);
  const kb = Math.round(asset.sizeBytes / 1024);
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          className={`wsp-ba-card${isSelected ? ' wsp-ba-card--selected' : ''}`}
          onClick={onClick}
          aria-pressed={isSelected}
        >
          <div className="wsp-ba-card-thumb">
            {src ? (
              <img src={src} alt={asset.originalFileName} className="wsp-ba-card-img" />
            ) : (
              <div className="wsp-ba-card-placeholder" aria-hidden="true" />
            )}
          </div>
          <div className="wsp-ba-card-meta">
            <span className="wsp-ba-card-name">{asset.originalFileName}</span>
            <span className="wsp-ba-card-size">{kb} KB</span>
          </div>
          {(isHeaderLogo || isDocLogo) && (
            <div className="wsp-ba-card-pills">
              {isHeaderLogo && <span className="wsp-ba-pill wsp-ba-pill--header">Header</span>}
              {isDocLogo && <span className="wsp-ba-pill wsp-ba-pill--doc">Doc</span>}
            </div>
          )}
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
          {asset.originalFileName}
          <Tooltip.Arrow className="ui-tooltip-arrow" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

function ConflictThumb({ contentUrl }: { contentUrl: string }) {
  const src = useProtectedImageUrl(contentUrl);
  if (!src) return null;
  return <img src={src} alt="" className="wsp-ba-conflict-thumb" />;
}

// ── Brand Colors tab ──────────────────────────────────────────────────────────

const HEX_RE = /^[0-9A-Fa-f]{6}$/;
const DEFAULT_COLOR = '1E3A5F';

function BrandColorsTab() {
  const { refetchWorkspace } = useWorkspace();
  const [primaryColor, setPrimaryColor] = React.useState<string>('');
  const [inputValue, setInputValue] = React.useState<string>('');
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/workspace/admin/brand/colors');
      const colors = (data as { colors: Record<string, string | null> }).colors;
      const color = colors['primary_color'] ?? '';
      setPrimaryColor(color);
      setInputValue(color);
    } catch {
      setError('Failed to load brand colors.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Close picker when clicking outside
  React.useEffect(() => {
    if (!pickerOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [pickerOpen]);

  const handlePickerChange = React.useCallback((hex: string) => {
    const raw = hex.replace(/^#/, '').toUpperCase();
    setInputValue(raw);
    setSaveError(null);
    setSaved(false);
  }, []);

  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/^#/, '').toUpperCase();
    setInputValue(raw);
    setSaveError(null);
    setSaved(false);
  }, []);

  const handleSave = React.useCallback(async () => {
    const value = inputValue.trim().toUpperCase();
    if (!HEX_RE.test(value)) {
      setSaveError('Enter a valid 6-character hex color (e.g. E46D03).');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await apiFetch('/api/workspace/admin/brand/colors/primary_color', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      setPrimaryColor(value);
      setSaved(true);
      await refetchWorkspace();
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [inputValue, refetchWorkspace]);

  const validHex = HEX_RE.test(inputValue.trim());
  const pickerColor = `#${validHex ? inputValue.trim() : primaryColor || DEFAULT_COLOR}`;

  if (isLoading) {
    return (
      <div className="wsp-bc-container">
        <div className="wsp-bc-loading">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wsp-bc-container">
        <PageErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="wsp-bc-container">
      <div className="wsp-bc-row">
        <label className="wsp-bc-label" htmlFor="wsp-bc-primary">
          Primary Color
        </label>
        <p className="wsp-bc-desc">
          Set the primary brand color used in exported documents such as estimates and SOWs.
        </p>
        <div className="wsp-bc-field" ref={popoverRef}>
          <button
            type="button"
            className="wsp-bc-swatch-btn"
            style={{ backgroundColor: pickerColor }}
            onClick={() => setPickerOpen((o) => !o)}
            aria-label="Open color picker"
            aria-expanded={pickerOpen}
          />
          <span className="wsp-bc-hash">#</span>
          <input
            id="wsp-bc-primary"
            type="text"
            className="wsp-bc-hex-input"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setPickerOpen(true)}
            maxLength={6}
            placeholder={DEFAULT_COLOR}
            spellCheck={false}
            aria-label="Hex color value"
          />
          {pickerOpen && (
            <div className="wsp-bc-popover">
              <HexColorPicker color={pickerColor} onChange={handlePickerChange} />
            </div>
          )}
        </div>
      </div>
      {saveError && <p className="wsp-bc-save-error">{saveError}</p>}
      <button
        type="button"
        className="wsp-bc-save-btn"
        onClick={() => void handleSave()}
        disabled={
          isSaving || saved || inputValue.trim().toUpperCase() === primaryColor.toUpperCase()
        }
      >
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function WorkspaceSettingsPage() {
  // `?tab=resources` opens a tab directly (the deal page's "Set rates" pop-up links here).
  const [pageSearchParams] = useSearchParams();
  const requestedTab = pageSearchParams.get('tab');
  const initialTab =
    requestedTab && WORKSPACE_TABS.some((t) => t.value === requestedTab)
      ? requestedTab
      : 'brand-assets';
  const { workspace, refetchWorkspace } = useWorkspace();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [assets, setAssets] = React.useState<BrandAsset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = React.useState(true);
  const [assetsError, setAssetsError] = React.useState<string | null>(null);

  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);

  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  const [panelPosition, setPanelPosition] = React.useState<'before' | 'after'>('after');
  const [confirmingUseCase, setConfirmingUseCase] = React.useState<
    'header' | 'doc' | 'cover' | 'coverPageLogo' | 'footer' | null
  >(null);
  const [isAssigning, setIsAssigning] = React.useState(false);
  const [assignError, setAssignError] = React.useState<string | null>(null);
  const [isUnassigning, setIsUnassigning] = React.useState(false);
  const [isSavingPosition, setIsSavingPosition] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);

  const headerLogoUuid = workspace?.headerLogoAssetUuid ?? null;
  const headerLogoPosition = (workspace?.headerLogoPosition as 'before' | 'after') ?? 'after';
  const docLogoUuid = workspace?.documentLogoAssetUuid ?? null;
  const coverBgUuid = workspace?.coverBackgroundAssetUuid ?? null;
  const coverPageLogoUuid = workspace?.coverPageLogoAssetUuid ?? null;
  const footerLogoUuid = workspace?.footerLogoAssetUuid ?? null;

  const selectedAsset = assets.find((a) => a.uuid === selectedUuid) ?? null;
  const panelOpen = selectedUuid !== null && selectedAsset !== null;

  const panelImageSrc = useProtectedImageUrl(selectedAsset?.contentUrl ?? null);

  const headerConflictAsset =
    confirmingUseCase === 'header' && headerLogoUuid && headerLogoUuid !== selectedUuid
      ? (assets.find((a) => a.uuid === headerLogoUuid) ?? null)
      : null;

  const docConflictAsset =
    confirmingUseCase === 'doc' && docLogoUuid && docLogoUuid !== selectedUuid
      ? (assets.find((a) => a.uuid === docLogoUuid) ?? null)
      : null;

  const coverConflictAsset =
    confirmingUseCase === 'cover' && coverBgUuid && coverBgUuid !== selectedUuid
      ? (assets.find((a) => a.uuid === coverBgUuid) ?? null)
      : null;

  const coverPageLogoConflictAsset =
    confirmingUseCase === 'coverPageLogo' && coverPageLogoUuid && coverPageLogoUuid !== selectedUuid
      ? (assets.find((a) => a.uuid === coverPageLogoUuid) ?? null)
      : null;

  const footerConflictAsset =
    confirmingUseCase === 'footer' && footerLogoUuid && footerLogoUuid !== selectedUuid
      ? (assets.find((a) => a.uuid === footerLogoUuid) ?? null)
      : null;

  const loadAssets = React.useCallback(async () => {
    setIsLoadingAssets(true);
    setAssetsError(null);
    try {
      const data = await apiFetch('/api/workspace/admin/assets');
      setAssets((data as { assets: BrandAsset[] }).assets ?? []);
    } catch {
      setAssetsError('Failed to load assets. Please refresh.');
    } finally {
      setIsLoadingAssets(false);
    }
  }, []);

  React.useEffect(() => {
    void loadAssets();
  }, [loadAssets]);

  React.useEffect(() => {
    setPanelPosition(headerLogoPosition);
    setConfirmingUseCase(null);
    setConfirmingDelete(false);
    setAssignError(null);
    setIsAssigning(false);
    setIsUnassigning(false);
    setIsSavingPosition(false);
    setIsDeleting(false);
  }, [selectedUuid, headerLogoPosition]);

  const handleFileInputChange = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (files.length === 0) return;

      setUploadError(null);

      const invalid = files.find((f) => !f.type.startsWith('image/'));
      if (invalid) {
        setUploadError('All files must be images (PNG, JPG, WebP, or GIF).');
        return;
      }
      const tooBig = files.find((f) => f.size > MAX_BYTES);
      if (tooBig) {
        setUploadError(`"${tooBig.name}" exceeds the 5 MB limit.`);
        return;
      }

      setIsUploading(true);
      try {
        const uploaded: BrandAsset[] = [];
        for (const file of files) {
          const formData = new FormData();
          formData.set('file', file);
          const result = await apiFetch('/api/workspace/admin/assets', {
            method: 'POST',
            body: formData,
          });
          uploaded.push((result as { asset: BrandAsset }).asset);
        }
        setAssets((prev) => [...uploaded.reverse(), ...prev]);
        setSelectedUuid(uploaded[uploaded.length - 1]?.uuid ?? null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        setUploadError(
          msg === 'UNSUPPORTED_IMAGE_TYPE'
            ? 'Unsupported image type. Use PNG, JPG, WebP, or GIF.'
            : msg === 'FILE_TOO_LARGE'
              ? 'Image must be 5 MB or smaller.'
              : 'Upload failed. Please try again.'
        );
      } finally {
        setIsUploading(false);
      }
    },
    []
  );

  const handleAssignHeaderLogo = React.useCallback(
    async (confirmed = false) => {
      if (!selectedUuid) return;

      if (!confirmed && headerLogoUuid && headerLogoUuid !== selectedUuid) {
        // Only show conflict dialog if we can display the conflicting asset.
        // If the asset isn't in the list (e.g. stale reference), skip and assign directly.
        const conflictAsset = assets.find((a) => a.uuid === headerLogoUuid);
        if (conflictAsset) {
          setConfirmingUseCase('header');
          return;
        }
      }

      setConfirmingUseCase(null);
      setAssignError(null);
      setIsAssigning(true);
      try {
        await apiFetch('/api/workspace/admin/brand/use-cases/header-logo', {
          method: 'PATCH',
          body: JSON.stringify({ assetUuid: selectedUuid, position: panelPosition }),
        });
        await refetchWorkspace();
      } catch {
        setAssignError('Failed to assign. Please try again.');
      } finally {
        setIsAssigning(false);
      }
    },
    [selectedUuid, headerLogoUuid, assets, panelPosition, refetchWorkspace]
  );

  const handleUnassignHeaderLogo = React.useCallback(async () => {
    setIsUnassigning(true);
    try {
      await apiFetch('/api/workspace/admin/brand/use-cases/header-logo', { method: 'DELETE' });
      await refetchWorkspace();
    } catch {
      // ignore
    } finally {
      setIsUnassigning(false);
    }
  }, [refetchWorkspace]);

  const handleAssignDocLogo = React.useCallback(
    async (confirmed = false) => {
      if (!selectedUuid) return;

      if (!confirmed && docLogoUuid && docLogoUuid !== selectedUuid) {
        const conflictAsset = assets.find((a) => a.uuid === docLogoUuid);
        if (conflictAsset) {
          setConfirmingUseCase('doc');
          return;
        }
      }

      setConfirmingUseCase(null);
      setAssignError(null);
      setIsAssigning(true);
      try {
        await apiFetch('/api/workspace/admin/brand/use-cases/document-logo', {
          method: 'PATCH',
          body: JSON.stringify({ assetUuid: selectedUuid }),
        });
        await refetchWorkspace();
      } catch {
        setAssignError('Failed to assign. Please try again.');
      } finally {
        setIsAssigning(false);
      }
    },
    [selectedUuid, docLogoUuid, assets, refetchWorkspace]
  );

  const handleUnassignDocLogo = React.useCallback(async () => {
    setIsUnassigning(true);
    try {
      await apiFetch('/api/workspace/admin/brand/use-cases/document-logo', { method: 'DELETE' });
      await refetchWorkspace();
    } catch {
      // ignore
    } finally {
      setIsUnassigning(false);
    }
  }, [refetchWorkspace]);

  const handleAssignCoverBg = React.useCallback(
    async (confirmed = false) => {
      if (!selectedUuid) return;
      if (!confirmed && coverBgUuid && coverBgUuid !== selectedUuid) {
        if (assets.find((a) => a.uuid === coverBgUuid)) {
          setConfirmingUseCase('cover');
          return;
        }
      }
      setConfirmingUseCase(null);
      setAssignError(null);
      setIsAssigning(true);
      try {
        await apiFetch('/api/workspace/admin/brand/use-cases/cover-background', {
          method: 'PATCH',
          body: JSON.stringify({ assetUuid: selectedUuid }),
        });
        await refetchWorkspace();
      } catch {
        setAssignError('Failed to assign. Please try again.');
      } finally {
        setIsAssigning(false);
      }
    },
    [selectedUuid, coverBgUuid, assets, refetchWorkspace]
  );

  const handleUnassignCoverBg = React.useCallback(async () => {
    setIsUnassigning(true);
    try {
      await apiFetch('/api/workspace/admin/brand/use-cases/cover-background', { method: 'DELETE' });
      await refetchWorkspace();
    } catch {
      // ignore
    } finally {
      setIsUnassigning(false);
    }
  }, [refetchWorkspace]);

  const handleAssignCoverPageLogo = React.useCallback(
    async (confirmed = false) => {
      if (!selectedUuid) return;
      if (!confirmed && coverPageLogoUuid && coverPageLogoUuid !== selectedUuid) {
        if (assets.find((a) => a.uuid === coverPageLogoUuid)) {
          setConfirmingUseCase('coverPageLogo');
          return;
        }
      }
      setConfirmingUseCase(null);
      setAssignError(null);
      setIsAssigning(true);
      try {
        await apiFetch('/api/workspace/admin/brand/use-cases/cover-page-logo', {
          method: 'PATCH',
          body: JSON.stringify({ assetUuid: selectedUuid }),
        });
        await refetchWorkspace();
      } catch {
        setAssignError('Failed to assign. Please try again.');
      } finally {
        setIsAssigning(false);
      }
    },
    [selectedUuid, coverPageLogoUuid, assets, refetchWorkspace]
  );

  const handleUnassignCoverPageLogo = React.useCallback(async () => {
    setIsUnassigning(true);
    try {
      await apiFetch('/api/workspace/admin/brand/use-cases/cover-page-logo', { method: 'DELETE' });
      await refetchWorkspace();
    } catch {
      // ignore
    } finally {
      setIsUnassigning(false);
    }
  }, [refetchWorkspace]);

  const handleAssignFooterLogo = React.useCallback(
    async (confirmed = false) => {
      if (!selectedUuid) return;
      if (!confirmed && footerLogoUuid && footerLogoUuid !== selectedUuid) {
        if (assets.find((a) => a.uuid === footerLogoUuid)) {
          setConfirmingUseCase('footer');
          return;
        }
      }
      setConfirmingUseCase(null);
      setAssignError(null);
      setIsAssigning(true);
      try {
        await apiFetch('/api/workspace/admin/brand/use-cases/footer-logo', {
          method: 'PATCH',
          body: JSON.stringify({ assetUuid: selectedUuid }),
        });
        await refetchWorkspace();
      } catch {
        setAssignError('Failed to assign. Please try again.');
      } finally {
        setIsAssigning(false);
      }
    },
    [selectedUuid, footerLogoUuid, assets, refetchWorkspace]
  );

  const handleUnassignFooterLogo = React.useCallback(async () => {
    setIsUnassigning(true);
    try {
      await apiFetch('/api/workspace/admin/brand/use-cases/footer-logo', { method: 'DELETE' });
      await refetchWorkspace();
    } catch {
      // ignore
    } finally {
      setIsUnassigning(false);
    }
  }, [refetchWorkspace]);

  const handleSavePosition = React.useCallback(async () => {
    if (!selectedUuid) return;
    setIsSavingPosition(true);
    try {
      await apiFetch('/api/workspace/admin/brand/use-cases/header-logo', {
        method: 'PATCH',
        body: JSON.stringify({ assetUuid: selectedUuid, position: panelPosition }),
      });
      await refetchWorkspace();
    } catch {
      // ignore
    } finally {
      setIsSavingPosition(false);
    }
  }, [selectedUuid, panelPosition, refetchWorkspace]);

  const handleDownload = React.useCallback(async () => {
    if (!selectedAsset) return;
    try {
      const token = await getAuthToken();
      const res = await fetch(selectedAsset.contentUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedAsset.originalFileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }, [selectedAsset]);

  const handleDeleteAsset = React.useCallback(async () => {
    if (!selectedUuid) return;
    setIsDeleting(true);
    try {
      await apiFetch(`/api/workspace/admin/assets/${selectedUuid}`, { method: 'DELETE' });
      setAssets((prev) => prev.filter((a) => a.uuid !== selectedUuid));
      setSelectedUuid(null);
      setConfirmingDelete(false);
      await refetchWorkspace();
    } catch {
      // ignore
    } finally {
      setIsDeleting(false);
    }
  }, [selectedUuid, refetchWorkspace]);

  const isHeaderLogo = React.useCallback(
    (uuid: string) => headerLogoUuid === uuid,
    [headerLogoUuid]
  );
  const isDocLogo = React.useCallback((uuid: string) => docLogoUuid === uuid, [docLogoUuid]);

  const selectedIsHeaderLogo = selectedUuid ? isHeaderLogo(selectedUuid) : false;
  const selectedIsDocLogo = selectedUuid ? isDocLogo(selectedUuid) : false;
  const selectedIsCoverBg = selectedUuid != null && coverBgUuid === selectedUuid;
  const selectedIsCoverPageLogo = selectedUuid != null && coverPageLogoUuid === selectedUuid;
  const selectedIsFooterLogo = selectedUuid != null && footerLogoUuid === selectedUuid;
  const positionChanged = panelPosition !== headerLogoPosition;

  // ── Left pane: asset grid ─────────────────────────────────────────────────

  const leftPane = (
    <div className="wsp-ba-left">
      <div className="wsp-ba-toolbar">
        <p className="wsp-ba-desc">
          Upload images for use across your workspace. Click any image to configure its use cases.
        </p>
        <button
          type="button"
          className="wsp-ba-upload-btn"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          onChange={handleFileInputChange}
          className="wsp-ba-file-input"
          aria-label="Upload brand asset images"
        />
      </div>

      {uploadError && (
        <p className="wsp-ba-upload-error" role="alert">
          {uploadError}
        </p>
      )}

      <div className="wsp-ba-grid">
        {isLoadingAssets ? (
          [0, 1, 2, 3].map((i) => (
            <div key={i} className="wsp-ba-card wsp-ba-card--skeleton" aria-hidden="true">
              <div className="wsp-ba-card-thumb">
                <SkeletonBlock />
              </div>
              <div className="wsp-ba-card-sk-name">
                <SkeletonBlock />
              </div>
              <div className="wsp-ba-card-sk-size">
                <SkeletonBlock />
              </div>
            </div>
          ))
        ) : assetsError ? (
          <div className="wsp-ba-error-state">
            <PageErrorState message={assetsError} onRetry={() => void loadAssets()} />
          </div>
        ) : assets.length === 0 ? (
          <p className="wsp-ba-empty">
            No images uploaded yet. Click &quot;Upload Image&quot; to add one.
          </p>
        ) : (
          assets.map((asset) => (
            <AssetCard
              key={asset.uuid}
              asset={asset}
              isSelected={selectedUuid === asset.uuid}
              isHeaderLogo={isHeaderLogo(asset.uuid)}
              isDocLogo={isDocLogo(asset.uuid)}
              onClick={() => setSelectedUuid((prev) => (prev === asset.uuid ? null : asset.uuid))}
            />
          ))
        )}
      </div>
    </div>
  );

  // ── Right pane: detail panel ──────────────────────────────────────────────

  const rightPane = selectedAsset ? (
    <div className="wsp-ba-panel">
      <div className="wsp-ba-panel-header">
        <Tooltip.Simple label={selectedAsset.originalFileName}>
          <span className="wsp-ba-panel-title">{selectedAsset.originalFileName}</span>
        </Tooltip.Simple>
        <button
          type="button"
          className="wsp-ba-panel-close"
          aria-label="Close panel"
          onClick={() => setSelectedUuid(null)}
        >
          ✕
        </button>
      </div>

      <div className="wsp-ba-panel-body">
        <div className="wsp-ba-panel-preview">
          {panelImageSrc ? (
            <img
              src={panelImageSrc}
              alt={selectedAsset.originalFileName}
              className="wsp-ba-panel-preview-img"
            />
          ) : (
            <div className="wsp-ba-panel-preview-placeholder" aria-hidden="true" />
          )}
        </div>

        <p className="wsp-ba-panel-meta">
          {selectedAsset.mimeType.split('/')[1]?.toUpperCase()} ·{' '}
          {Math.round(selectedAsset.sizeBytes / 1024)} KB
          {selectedAsset.widthPx && selectedAsset.heightPx
            ? ` · ${selectedAsset.widthPx}×${selectedAsset.heightPx}`
            : ''}
        </p>

        <div className="wsp-ba-section">
          <p className="wsp-ba-section-label">Use Cases</p>

          {/* Header Logo */}
          <div className="wsp-ba-use-case-row">
            <div className="wsp-ba-use-case-info">
              <span className="wsp-ba-use-case-name">Header Logo</span>
              <span className="wsp-ba-use-case-desc">Shown in the global header</span>
            </div>
            {selectedIsHeaderLogo ? (
              <button
                type="button"
                className="wsp-ba-use-case-btn wsp-ba-use-case-btn--active"
                disabled={isUnassigning}
                onClick={() => void handleUnassignHeaderLogo()}
              >
                {isUnassigning ? 'Removing…' : 'Assigned'}
              </button>
            ) : (
              <button
                type="button"
                className="wsp-ba-use-case-btn"
                disabled={isAssigning}
                onClick={() => void handleAssignHeaderLogo()}
              >
                Assign
              </button>
            )}
          </div>

          {confirmingUseCase === 'header' && headerConflictAsset && (
            <div className="wsp-ba-conflict">
              <ConflictThumb contentUrl={headerConflictAsset.contentUrl} />
              <div className="wsp-ba-conflict-body">
                <p className="wsp-ba-conflict-msg">
                  Header currently uses <strong>{headerConflictAsset.originalFileName}</strong>.
                  Replace with this image?
                </p>
                <div className="wsp-ba-conflict-actions">
                  <button
                    type="button"
                    className="wsp-ba-conflict-confirm-btn"
                    disabled={isAssigning}
                    onClick={() => void handleAssignHeaderLogo(true)}
                  >
                    {isAssigning ? 'Replacing…' : 'Replace'}
                  </button>
                  <button
                    type="button"
                    className="wsp-ba-conflict-cancel-btn"
                    onClick={() => setConfirmingUseCase(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Doc Logo */}
          <div className="wsp-ba-use-case-row">
            <div className="wsp-ba-use-case-info">
              <span className="wsp-ba-use-case-name">Doc Logo</span>
              <span className="wsp-ba-use-case-desc">
                The logo on exported documents: SOWs and estimates. If empty, SOWs use the Cover
                Page Logo, then the Header Logo.
              </span>
            </div>
            {selectedIsDocLogo ? (
              <button
                type="button"
                className="wsp-ba-use-case-btn wsp-ba-use-case-btn--active"
                disabled={isUnassigning}
                onClick={() => void handleUnassignDocLogo()}
              >
                {isUnassigning ? 'Removing…' : 'Assigned'}
              </button>
            ) : (
              <button
                type="button"
                className="wsp-ba-use-case-btn"
                disabled={isAssigning}
                onClick={() => void handleAssignDocLogo()}
              >
                Assign
              </button>
            )}
          </div>

          {confirmingUseCase === 'doc' && docConflictAsset && (
            <div className="wsp-ba-conflict">
              <ConflictThumb contentUrl={docConflictAsset.contentUrl} />
              <div className="wsp-ba-conflict-body">
                <p className="wsp-ba-conflict-msg">
                  Doc logo currently uses <strong>{docConflictAsset.originalFileName}</strong>.
                  Replace with this image?
                </p>
                <div className="wsp-ba-conflict-actions">
                  <button
                    type="button"
                    className="wsp-ba-conflict-confirm-btn"
                    disabled={isAssigning}
                    onClick={() => void handleAssignDocLogo(true)}
                  >
                    {isAssigning ? 'Replacing…' : 'Replace'}
                  </button>
                  <button
                    type="button"
                    className="wsp-ba-conflict-cancel-btn"
                    onClick={() => setConfirmingUseCase(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Cover Background */}
          <div className="wsp-ba-use-case-row">
            <div className="wsp-ba-use-case-info">
              <span className="wsp-ba-use-case-name">Cover Background</span>
              <span className="wsp-ba-use-case-desc">
                Default cover-page image for generated SOWs
              </span>
            </div>
            {selectedIsCoverBg ? (
              <button
                type="button"
                className="wsp-ba-use-case-btn wsp-ba-use-case-btn--active"
                disabled={isUnassigning}
                onClick={() => void handleUnassignCoverBg()}
              >
                {isUnassigning ? 'Removing…' : 'Assigned'}
              </button>
            ) : (
              <button
                type="button"
                className="wsp-ba-use-case-btn"
                disabled={isAssigning}
                onClick={() => void handleAssignCoverBg()}
              >
                Assign
              </button>
            )}
          </div>

          {confirmingUseCase === 'cover' && coverConflictAsset && (
            <div className="wsp-ba-conflict">
              <ConflictThumb contentUrl={coverConflictAsset.contentUrl} />
              <div className="wsp-ba-conflict-body">
                <p className="wsp-ba-conflict-msg">
                  Cover background currently uses{' '}
                  <strong>{coverConflictAsset.originalFileName}</strong>. Replace with this image?
                </p>
                <div className="wsp-ba-conflict-actions">
                  <button
                    type="button"
                    className="wsp-ba-conflict-confirm-btn"
                    disabled={isAssigning}
                    onClick={() => void handleAssignCoverBg(true)}
                  >
                    {isAssigning ? 'Replacing…' : 'Replace'}
                  </button>
                  <button
                    type="button"
                    className="wsp-ba-conflict-cancel-btn"
                    onClick={() => setConfirmingUseCase(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Cover Page Logo */}
          <div className="wsp-ba-use-case-row">
            <div className="wsp-ba-use-case-info">
              <span className="wsp-ba-use-case-name">Cover Page Logo</span>
              <span className="wsp-ba-use-case-desc">
                Only for SOW templates with a cover-page logo spot. Default SOWs use the Doc Logo,
                and fall back to this one when no Doc Logo is set.
              </span>
            </div>
            {selectedIsCoverPageLogo ? (
              <button
                type="button"
                className="wsp-ba-use-case-btn wsp-ba-use-case-btn--active"
                disabled={isUnassigning}
                onClick={() => void handleUnassignCoverPageLogo()}
              >
                {isUnassigning ? 'Removing…' : 'Assigned'}
              </button>
            ) : (
              <button
                type="button"
                className="wsp-ba-use-case-btn"
                disabled={isAssigning}
                onClick={() => void handleAssignCoverPageLogo()}
              >
                Assign
              </button>
            )}
          </div>

          {confirmingUseCase === 'coverPageLogo' && coverPageLogoConflictAsset && (
            <div className="wsp-ba-conflict">
              <ConflictThumb contentUrl={coverPageLogoConflictAsset.contentUrl} />
              <div className="wsp-ba-conflict-body">
                <p className="wsp-ba-conflict-msg">
                  Cover page logo currently uses{' '}
                  <strong>{coverPageLogoConflictAsset.originalFileName}</strong>. Replace with this
                  image?
                </p>
                <div className="wsp-ba-conflict-actions">
                  <button
                    type="button"
                    className="wsp-ba-conflict-confirm-btn"
                    disabled={isAssigning}
                    onClick={() => void handleAssignCoverPageLogo(true)}
                  >
                    {isAssigning ? 'Replacing…' : 'Replace'}
                  </button>
                  <button
                    type="button"
                    className="wsp-ba-conflict-cancel-btn"
                    onClick={() => setConfirmingUseCase(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer Logo */}
          <div className="wsp-ba-use-case-row">
            <div className="wsp-ba-use-case-info">
              <span className="wsp-ba-use-case-name">Footer Logo</span>
              <span className="wsp-ba-use-case-desc">Default footer logo for generated SOWs</span>
            </div>
            {selectedIsFooterLogo ? (
              <button
                type="button"
                className="wsp-ba-use-case-btn wsp-ba-use-case-btn--active"
                disabled={isUnassigning}
                onClick={() => void handleUnassignFooterLogo()}
              >
                {isUnassigning ? 'Removing…' : 'Assigned'}
              </button>
            ) : (
              <button
                type="button"
                className="wsp-ba-use-case-btn"
                disabled={isAssigning}
                onClick={() => void handleAssignFooterLogo()}
              >
                Assign
              </button>
            )}
          </div>

          {confirmingUseCase === 'footer' && footerConflictAsset && (
            <div className="wsp-ba-conflict">
              <ConflictThumb contentUrl={footerConflictAsset.contentUrl} />
              <div className="wsp-ba-conflict-body">
                <p className="wsp-ba-conflict-msg">
                  Footer logo currently uses <strong>{footerConflictAsset.originalFileName}</strong>
                  . Replace with this image?
                </p>
                <div className="wsp-ba-conflict-actions">
                  <button
                    type="button"
                    className="wsp-ba-conflict-confirm-btn"
                    disabled={isAssigning}
                    onClick={() => void handleAssignFooterLogo(true)}
                  >
                    {isAssigning ? 'Replacing…' : 'Replace'}
                  </button>
                  <button
                    type="button"
                    className="wsp-ba-conflict-cancel-btn"
                    onClick={() => setConfirmingUseCase(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {assignError && (
            <p className="wsp-ba-upload-error" role="alert">
              {assignError}
            </p>
          )}

          {selectedIsHeaderLogo && (
            <div className="wsp-ba-position">
              <p className="wsp-ba-position-label">Position in header</p>
              <div className="wsp-ba-position-options">
                <button
                  type="button"
                  className="wsp-ba-position-option"
                  data-selected={panelPosition === 'before' || undefined}
                  aria-pressed={panelPosition === 'before'}
                  onClick={() => setPanelPosition('before')}
                >
                  <div className="wsp-ba-position-mock">
                    {panelImageSrc && (
                      <img
                        src={panelImageSrc}
                        alt=""
                        className="wsp-ba-position-mock-img"
                        aria-hidden="true"
                      />
                    )}
                    <span className="wsp-ba-position-mock-wordmark">
                      {workspace?.name ?? 'Workspace'}
                    </span>
                  </div>
                  <span className="wsp-ba-position-option-label">Before workspace name</span>
                </button>
                <button
                  type="button"
                  className="wsp-ba-position-option"
                  data-selected={panelPosition === 'after' || undefined}
                  aria-pressed={panelPosition === 'after'}
                  onClick={() => setPanelPosition('after')}
                >
                  <div className="wsp-ba-position-mock">
                    <span className="wsp-ba-position-mock-wordmark">
                      {workspace?.name ?? 'Workspace'}
                    </span>
                    {panelImageSrc && (
                      <img
                        src={panelImageSrc}
                        alt=""
                        className="wsp-ba-position-mock-img"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <span className="wsp-ba-position-option-label">After workspace name</span>
                </button>
              </div>
              <button
                type="button"
                className="wsp-ba-save-position-btn"
                disabled={isSavingPosition || !positionChanged}
                onClick={() => void handleSavePosition()}
              >
                {isSavingPosition ? 'Saving…' : 'Save Position'}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="wsp-ba-panel-footer">
        <button
          type="button"
          className="wsp-ba-delete-btn"
          onClick={() => setConfirmingDelete(true)}
        >
          Delete
        </button>
        <button type="button" className="wsp-ba-download-btn" onClick={() => void handleDownload()}>
          Download
        </button>
      </div>
    </div>
  ) : null;

  // ── Page shell ────────────────────────────────────────────────────────────

  return (
    <div className="wsp-page">
      <Tabs
        items={WORKSPACE_TABS}
        defaultValue={initialTab}
        ariaLabel="Workspace settings sections"
        variant="subnav"
        size="md"
        className="page-tabs"
      >
        <TabsContent value="brand-assets" className="page-tab-content">
          <div className="page-content">
            <SplitView
              left={leftPane}
              right={rightPane}
              rightOpen={panelOpen}
              rightWidth={300}
              resizable
              className="wsp-ba-split"
              leftClassName="wsp-ba-left-pane"
              rightClassName="wsp-ba-split-right"
            />
          </div>
        </TabsContent>
        <TabsContent value="brand-colors" className="page-tab-content">
          <div className="page-content">
            <BrandColorsTab />
          </div>
        </TabsContent>
        <TabsContent value="resources" className="page-tab-content">
          <div className="page-content">
            <ResourcesTab />
          </div>
        </TabsContent>
        <TabsContent value="api-keys" className="page-tab-content">
          <div className="page-content">
            <ApiKeysTab />
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDeleteDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Delete image?"
        confirmKeyword={selectedAsset?.originalFileName ?? ''}
        description="This action cannot be undone. The image will be permanently deleted."
        isPending={isDeleting}
        onConfirm={handleDeleteAsset}
      />
    </div>
  );
}
